import { WorkflowAction } from './WorkflowAction';

// The status the autonomous check publishes on denial_check_result: 'match' when the list
// flagged the patient, 'clear' when nothing matched (OLIO-2463). The check never asks a person
// to adjudicate — a match is surfaced as a flag on the completed card.
export const DENIAL_RESULT = { MATCH: 'match', CLEAR: 'clear' } as const;

// A surfaced denial-list match. Read shape is camelCase — the API stores the value with
// snake_case keys and OliveBranch camelCases them on the response.
export interface DenialMatch {
  denialListItemId: string;
  patientName: string | null;
  dateOfBirth: string | null;
  sex: string | null;
  reason: string | null;
  dateAdded: string | null;
}

interface DenialResult {
  status?: string;
  matches?: DenialMatch[];
}

// The DenialCheck action kind. The check runs and completes autonomously, publishing
// denial_check_result as a structured { status, matches }. A completed match presents as a flag
// (orange "attention") on the otherwise-completed card; the matched entries feed a read-only
// slideout. Everything reads from THIS instance's own output (design §6c), so a completed/re-run
// card reflects exactly what that run produced.
// The match details the API folds into denial_check_result; the presentation (a completed match
// surfacing as `flagged`) is derived server-side via effective_status, so this model exists only to
// expose the match data the Review Flag slideout renders.
export class DenialCheckAction extends WorkflowAction {
  private get result(): DenialResult {
    const value = this.outputFor('denial_check_result');
    return value && typeof value === 'object' ? (value as DenialResult) : {};
  }

  get determination(): string | undefined {
    return this.result.status;
  }

  get isMatch(): boolean {
    return this.determination === DENIAL_RESULT.MATCH;
  }

  // The matched entries the read-only slideout renders (empty on a clear result).
  get matchedEntries(): DenialMatch[] {
    const matches = this.result.matches;
    return Array.isArray(matches) ? matches : [];
  }

  // The patient name the check ran against — from this instance's input snapshot, so the
  // no-match heading shows exactly who was checked even if demographics later change.
  get checkedName(): string {
    const value = (name: string) => this.inputs.find((input) => input.name === name)?.value ?? '';
    return `${value('patient_first_name')} ${value('patient_last_name')}`.trim();
  }
}
